import { type UseFormReturn, type UseFormSetValue } from 'react-hook-form'

import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { trans, type TranslationLanguage } from 'i18n/translate'
import {
  ObservabilityToolType,
  StatusCheckCategory,
  type StatusCheckHeader,
  type StatusCheckTeamExternalIntegration,
  type TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus,
  type TeamExternalIntegrationResponse
} from 'types/api/status-checks/status-check'
import { isPrivateEdition } from 'utils/app-config'
import { awsStatusCheckIntegration } from 'utils/status-checks/status-check-integrations-util-aws'
import { OBSERVABILITY_TOOL_DISPLAY_NAME } from 'utils/status-checks/status-checks-utils'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  computeResponseBodyEvaluation,
  convertToBaseUrl,
  defaultStatusCheckEvaluationConfiguration,
  getDomainNameFromIntegration,
  getIntegrationTypeFromObservabilityToolTypeAndDomain,
  prepareHeaders
} from 'utils/team-external-integrations/team-external-integrations-util'
import {
  AUTHENTICATION_URL_INTEGRATIONS,
  DOMAIN_NAME_INTEGRATIONS,
  ENDPOINT_URLS,
  URL_INTEGRATIONS,
  URL_INTEGRATIONS_PNI
} from 'utils/third-party'

import type { AuthenticationRequestBody } from 'queries/external-integrations/types'

/**
 * Types, Interfaces, Classes
 */
export type AWSDomain = {
  name: string
  integrationType: IntegrationType.AWS
}

export type ObservabilityToolDomain = {
  name: string
  integrationType?: IntegrationType
}

export type ObservabilityTool = {
  name: string
  type?: ObservabilityToolType
  integrationType?: IntegrationType
  domains?: ObservabilityToolDomain[]
  selectedDomain?: string
}

export enum AWSAuthenticationType {
  IAMRole = 'IAM Role',
  ServiceAccount = 'Service Account'
}

export enum AWSRoleCreationMethod {
  CloudFormation = 'Cloud Formation',
  Manual = 'Manual'
}

export enum AuthenticationType {
  AWSSecretsManager = 'AWS Secrets Manager',
  AuthorizationHeader = 'Authorization Header'
}

export interface AuthenticationFormValues {
  apiBaseUrl?: string
  authentication?: {
    username: string
    password: string
  }
  authenticationType?: AuthenticationType
  authenticationUrl?: string
  awsAccessKeyID?: string
  awsAuthenticationType?: AWSAuthenticationType
  awsIamRoleArn?: string
  awsRoleCreationMethod?: AWSRoleCreationMethod
  awsSecretAccessKey?: string
  customDomainName?: string
  domain?: ObservabilityToolDomain
  externalIntegration?: TeamExternalIntegration
  headers?: StatusCheckHeader[]
  isPrivateNetworkIntegration?: boolean
  monitorUrl?: string
  multipleRegions?: boolean
  observabilityTool: ObservabilityTool
  observabilityToolNickname?: string
  // generic secret field for when authenticationType is AWSSecretsManager (for now, this will be an ARN value as we only support AWS Secrets Manager; we may expand support for other solutions like HCP Vault, etc.)
  secret?: string
}

/**
 * Constants
 */
export const OBSERVABILITY_TOOLS: ObservabilityTool[] = [
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.DATADOG],
    type: ObservabilityToolType.DATADOG,
    integrationType: IntegrationType.Datadog, // Initial value
    domains: [
      { name: 'US1', integrationType: IntegrationType.Datadog },
      { name: 'US3', integrationType: IntegrationType.DatadogUS3 },
      { name: 'US5', integrationType: IntegrationType.DatadogUS5 },
      { name: 'EU', integrationType: IntegrationType.DatadogEU },
      { name: 'US1 FED', integrationType: IntegrationType.DatadogUS1Fed }
    ]
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.PAGERDUTY],
    type: ObservabilityToolType.PAGERDUTY,
    integrationType: IntegrationType.Pagerduty,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.NEWRELIC],
    type: ObservabilityToolType.NEWRELIC,
    integrationType: IntegrationType.Newrelic,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.GRAFANA],
    type: ObservabilityToolType.GRAFANA,
    integrationType: IntegrationType.Grafana,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.DYNATRACE],
    type: ObservabilityToolType.DYNATRACE,
    integrationType: IntegrationType.Dynatrace,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.APPDYNAMICS],
    type: ObservabilityToolType.APPDYNAMICS,
    integrationType: IntegrationType.Appdynamics,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.PROMETHEUS],
    type: ObservabilityToolType.PROMETHEUS,
    integrationType: IntegrationType.Prometheus,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.AWS],
    type: ObservabilityToolType.AWS,
    integrationType: IntegrationType.AWS,
    domains: []
  },
  {
    name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.CUSTOM],
    type: ObservabilityToolType.CUSTOM,
    integrationType: IntegrationType.Custom,
    domains: []
  }
]

export function getBinaryRadioOptions(language: TranslationLanguage) {
  return [
    {
      label: trans('no', {}, language),
      value: false
    },
    {
      label: trans('yes', {}, language),
      value: true
    }
  ]
}

/**
 * Extracts the proper integration URL to use based on its type and url value. Certain integration types rely
 * directly on the URL field, while others follow more complex formatting rules, otherwise having static URLs.
 */
function getIntegrationUrl({
  isPrivateNetworkIntegration,
  type,
  url
}: TeamExternalIntegration) {
  const shouldUseIntegrationUrl = URL_INTEGRATIONS.has(type)
  const shouldUseIntegrationUrlWithTrailingSlash =
    isPrivateNetworkIntegration && URL_INTEGRATIONS_PNI.has(type)

  if (shouldUseIntegrationUrlWithTrailingSlash) {
    return url?.endsWith('/') ? url : `${url}/`
  }

  if (shouldUseIntegrationUrl) return url

  return DOMAIN_NAME_INTEGRATIONS.has(type)
    ? convertToBaseUrl(type, url)
    : ENDPOINT_URLS[type]
}

export function getAWSIntegrationSpecificFormValues(
  integration: TeamExternalIntegration | StatusCheckTeamExternalIntegration
) {
  if (integration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN) {
    return {
      awsIamRoleArn: integration.integrationSpecificValues?.AWS_IAM_ROLE_ARN
    }
  }

  return {
    awsAccessKeyID: integration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID,
    awsSecretAccessKey:
      integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY
  }
}

export function getAWSIntegrationSpecificValues(
  integration: TeamExternalIntegration | StatusCheckTeamExternalIntegration
) {
  if (integration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN) {
    return {
      AWS_IAM_ROLE_ARN: integration.integrationSpecificValues?.AWS_IAM_ROLE_ARN
    }
  }

  return {
    AWS_ACCESS_KEY_ID:
      integration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY:
      integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY
  }
}

/**
 * Builds default Edit Authentication form values for editing a Team External Integration
 */
export function getDefaultAuthenticationFormValues(): AuthenticationFormValues {
  return {
    apiBaseUrl: '',
    authentication: { username: '', password: '' },
    authenticationType: undefined,
    authenticationUrl: '',
    awsAccessKeyID: '',
    awsIamRoleArn: '',
    awsSecretAccessKey: '',
    awsAuthenticationType: AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    customDomainName: '',
    domain: { name: '' },
    headers: [],
    isPrivateNetworkIntegration: false,
    multipleRegions: false,
    observabilityTool: { name: '' },
    observabilityToolNickname: '',
    secret: ''
  }
}

export function getDefaultAuthenticationFormValuesFromIntegration(
  externalIntegration: TeamExternalIntegration
): AuthenticationFormValues {
  const {
    arn,
    headers,
    integrationSpecificValues,
    isPrivateNetworkIntegration,
    name,
    observabilityToolType,
    type: integrationType,
    url
  } = externalIntegration
  const { AWS_IAM_ROLE_ARN } = integrationSpecificValues ?? {}

  const isAWSObservabilityTool =
    observabilityToolType === ObservabilityToolType.AWS
  const isCustomObservabilityTool =
    observabilityToolType === ObservabilityToolType.CUSTOM
  const isPrometheusObservabilityTool =
    observabilityToolType === ObservabilityToolType.PROMETHEUS

  const observabilityToolName = isCustomObservabilityTool
    ? name
    : OBSERVABILITY_TOOLS.find(({ type }) => type === observabilityToolType)
        ?.name

  const observabilityTool: ObservabilityTool = {
    name: observabilityToolName ?? '',
    type: observabilityToolType,
    integrationType
  }

  const domainName = getDomainNameFromIntegration(externalIntegration)

  // Configures any new custom domains to the observability tool so they are immediately available
  const domains =
    isCustomObservabilityTool && domainName
      ? [{ name: domainName, integrationType: IntegrationType.Custom }]
      : OBSERVABILITY_TOOLS.find(
          (staticObservabilityTool) =>
            staticObservabilityTool.type === observabilityTool.type
        )?.domains

  observabilityTool.domains = domains ?? undefined

  const customDomainName =
    isCustomObservabilityTool && Boolean(domains?.length)
      ? domainName ?? ''
      : ''

  const awsAuthenticationType = AWS_IAM_ROLE_ARN
    ? AWSAuthenticationType.IAMRole
    : AWSAuthenticationType.ServiceAccount

  const authentication =
    isPrometheusObservabilityTool && headers?.[0]?.value
      ? { username: '******', password: '******' }
      : { username: '', password: '' }

  return {
    apiBaseUrl: AUTHENTICATION_URL_INTEGRATIONS.has(integrationType) ? '' : url,
    authenticationUrl: AUTHENTICATION_URL_INTEGRATIONS.has(integrationType)
      ? url
      : '',
    awsAuthenticationType,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    awsIamRoleArn: AWS_IAM_ROLE_ARN ?? '',
    customDomainName,
    domain: domainName ? { name: domainName, integrationType } : { name: '' },
    externalIntegration,
    isPrivateNetworkIntegration: isPrivateNetworkIntegration ?? false,
    multipleRegions: Boolean(customDomainName),
    observabilityTool,
    ...(isCustomObservabilityTool && {
      observabilityToolNickname: observabilityToolName
    }),
    ...(isAWSObservabilityTool &&
      getAWSIntegrationSpecificFormValues(externalIntegration)),
    ...(authentication && { authentication }),
    ...(arn
      ? {
          authenticationType: AuthenticationType.AWSSecretsManager,
          secret: arn
        }
      : {
          authenticationType: AuthenticationType.AuthorizationHeader,
          headers: headers ?? []
        })
  }
}

function getAwsDomainFromService(
  domains: AWSDomain[],
  selectedAwsAccountId?: string
): ObservabilityToolDomain {
  const defaultDomain: AWSDomain = {
    name: '',
    integrationType: IntegrationType.AWS
  }

  // No domains available, return the default domain
  if (domains.length === 0) {
    return defaultDomain
  }

  // If the AWS account id is not available, return the first domain
  if (selectedAwsAccountId) {
    return (
      domains.find((d) => d.name === selectedAwsAccountId) || {
        name: selectedAwsAccountId,
        integrationType: IntegrationType.AWS
      }
    )
  }

  return {
    ...defaultDomain,
    ...domains[0]
  }
}

export function getAWSOnboardingAuthenticationDefaultValues(
  externalIntegrations: TeamExternalIntegration[],
  selectedAwsAccountId?: string
): AuthenticationFormValues {
  const domains = getAWSDomains(externalIntegrations)
  const domain = getAwsDomainFromService(domains, selectedAwsAccountId)

  const observabilityTool: ObservabilityTool = {
    name: 'AWS',
    type: ObservabilityToolType.AWS,
    integrationType: IntegrationType.AWS,
    domains,
    selectedDomain: domain.name
  }

  const externalIntegration =
    externalIntegrations.find(
      ({ domain: integrationDomain, type }) =>
        type === IntegrationType.AWS &&
        integrationDomain &&
        integrationDomain === domain.name
    ) ?? awsStatusCheckIntegration

  return {
    apiBaseUrl: '',
    authenticationUrl: externalIntegration?.url || '',
    awsAccessKeyID:
      externalIntegration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID || '',
    awsIamRoleArn:
      externalIntegration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN || '',
    awsSecretAccessKey:
      externalIntegration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY ||
      '',
    awsAuthenticationType: isPrivateEdition()
      ? AWSAuthenticationType.ServiceAccount
      : AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    customDomainName: '',
    domain,
    externalIntegration,
    headers: [],
    isPrivateNetworkIntegration: false,
    multipleRegions: false,
    observabilityTool,
    observabilityToolNickname: ''
  }
}

/**
 * Gets the external Integration that matches the passed in Observability Tool
 */
export function getExternalIntegrationForObservabilityTool(
  observabilityTool: ObservabilityTool | undefined,
  externalIntegrations: TeamExternalIntegration[],
  isCreatingNewAuthentication = false
) {
  if (!observabilityTool) return

  if (observabilityTool.type === ObservabilityToolType.CUSTOM) {
    // Custom external integrations are match to the selected observability tool by Name
    return externalIntegrations.find(
      ({ name }) => name === observabilityTool.name
    )
  }

  if (observabilityTool.type === ObservabilityToolType.AWS) {
    /*
     * when user is creating a new AWS account (aka domain)
     * they either won't have entered a domain value yet
     * or they have entered one that doesn't exist in externalIntegration yet
     * either way - return the first instance of the AWS that has
     * no `domain` value - this is a "clean" instance we hardcode locally
     */
    const existingAWSExternalIntegration = externalIntegrations.find(
      ({ domain, observabilityToolType }) =>
        observabilityToolType === observabilityTool.type &&
        domain === observabilityTool.selectedDomain
    )

    if (
      !observabilityTool.selectedDomain ||
      isCreatingNewAuthentication ||
      (observabilityTool.selectedDomain && !existingAWSExternalIntegration)
    ) {
      return externalIntegrations.find(
        ({ domain, type }) =>
          type === observabilityTool.integrationType && !domain
      )
    }

    return existingAWSExternalIntegration
  }

  // Non-custom external integrations are matched to the selected observability tool by IntegrationType
  return externalIntegrations.find(
    (integration) => integration.type === observabilityTool.integrationType
  )
}

/**
 * Compares the currently selected Observability Tool against an existing integrations list,
 * returning true if the integration exists and is authenticated.
 */
export function isSelectedObservabilityToolAuthenticated(
  observabilityTool: ObservabilityTool,
  externalIntegrations: TeamExternalIntegration[]
): boolean {
  const externalIntegration = getExternalIntegrationForObservabilityTool(
    observabilityTool,
    externalIntegrations
  )

  return (
    externalIntegration?.lastAuthenticationStatus ===
    TeamExternalIntegrationAuthenticationStatus.Authenticated
  )
}

export function setCustomObservabilityToolSpecificValues(
  observabilityTool: ObservabilityTool,
  integration: TeamExternalIntegration,
  setValue: UseFormSetValue<StatusCheckFormValues | AuthenticationFormValues>
) {
  if (integration.isConfigured) {
    setValue('observabilityToolNickname', observabilityTool.name)
  } else {
    // resetField does not always work here for some reason
    setValue('observabilityToolNickname', '')
  }
}

export function setAWSObservabilityToolSpecificValues(
  integration: TeamExternalIntegration,
  formValues: StatusCheckFormValues | AuthenticationFormValues,
  setValue: UseFormSetValue<StatusCheckFormValues | AuthenticationFormValues>
) {
  const { AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY, AWS_IAM_ROLE_ARN } =
    integration.integrationSpecificValues || {}
  const isServiceAccountIntegration =
    Boolean(AWS_ACCESS_KEY_ID) || Boolean(AWS_SECRET_ACCESS_KEY)

  // Set the various AWS authentication form values to the values from the integration if it is already configured
  if (integration.isConfigured) {
    if (isServiceAccountIntegration) {
      setValue('awsAccessKeyID', AWS_ACCESS_KEY_ID)
      setValue('awsSecretAccessKey', AWS_SECRET_ACCESS_KEY)
    } else {
      setValue('awsIamRoleArn', AWS_IAM_ROLE_ARN)
    }

    setValue(
      'observabilityTool.selectedDomain',
      integration.domain ?? undefined
    )
  } else {
    // Set the AWS authentication form values to either empty or existing form values (in the case of creating a new AWS authentication only)
    setValue('awsAccessKeyID', formValues.awsAccessKeyID || '')
    setValue('awsSecretAccessKey', formValues.awsSecretAccessKey || '')
    setValue('awsIamRoleArn', formValues.awsIamRoleArn || '')
  }

  // Only set the AWS authentication type and role creation method if they are not already set (in the case of creating/editing an AWS authentication only)
  if (!formValues.awsAuthenticationType && !formValues.awsRoleCreationMethod) {
    if (isServiceAccountIntegration) {
      setValue('awsAuthenticationType', AWSAuthenticationType.ServiceAccount)
    } else {
      setValue('awsAuthenticationType', AWSAuthenticationType.IAMRole)
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.CloudFormation)
    }
  }
}

/**
 * Handles building and setting relevant form values when selecting a new observability tool and/or domain selection.
 */
export function setObservabilityToolAndDomain(
  observabilityTool: ObservabilityTool,
  domain: ObservabilityToolDomain | null,
  integration: TeamExternalIntegration,
  form: UseFormReturn<StatusCheckFormValues | AuthenticationFormValues>
) {
  const { clearErrors, getValues, resetField, setValue } = form
  const newResponseBodyEvaluation = computeResponseBodyEvaluation(integration)

  if (newResponseBodyEvaluation) {
    setValue('evaluationConfiguration', {
      ...getValues('evaluationConfiguration'),
      responseBodyEvaluation: newResponseBodyEvaluation
    })
  } else {
    setValue(
      'evaluationConfiguration',
      defaultStatusCheckEvaluationConfiguration
    )
  }

  if (domain?.name) {
    setValue('domain', domain)
  } else if (observabilityTool.domains?.length) {
    // set domain value to first domain in list
    setValue('domain', observabilityTool.domains[0])
  } else {
    // reset domain value
    resetField('domain')
  }

  /*
   * For custom observability tools, set the observability tool nickname to the integration name if the integration is configured.
   * For all other observability tools, reset the observability tool nickname to an empty string.
   */
  if (
    observabilityTool.type === ObservabilityToolType.CUSTOM &&
    integration.isConfigured
  ) {
    setValue('observabilityToolNickname', observabilityTool.name)
  } else {
    // resetField does not always work here for some reason
    setValue('observabilityToolNickname', '')
  }

  if (
    observabilityTool.type !== ObservabilityToolType.CUSTOM &&
    observabilityTool.type !== ObservabilityToolType.AWS
  ) {
    setValue('apiBaseUrl', integration.url)
    resetField('authenticationUrl')
  } else {
    setValue('authenticationUrl', integration.url)
    resetField('apiBaseUrl')
  }

  // Dynatrace status checks do not use the UNKNOWN category, so default this value to ERRORS instead
  if (observabilityTool.type === ObservabilityToolType.DYNATRACE) {
    setValue('category', StatusCheckCategory.ERRORS)
  }

  setValue('statusCheckName', '')
  setValue('externalIntegration', integration)
  setValue(
    'isPrivateNetworkIntegration',
    integration.isPrivateNetworkIntegration
  )

  setValue('headers', integration.headers ?? [])
  setValue('observabilityTool', observabilityTool)

  if (observabilityTool.type === ObservabilityToolType.AWS) {
    setAWSObservabilityToolSpecificValues(integration, getValues(), setValue)
  }

  resetField('monitorUrl')

  clearErrors()
}

export function getAWSDomains(
  externalIntegrations: TeamExternalIntegration[]
): AWSDomain[] {
  return externalIntegrations
    .filter(
      (externalIntegration) =>
        externalIntegration.observabilityToolType === ObservabilityToolType.AWS
    )
    .map((awsIntegration) => ({
      name: awsIntegration.domain ?? '',
      integrationType: IntegrationType.AWS
    }))
}

// Configures any new custom domains to the observability tool so they are immediately available
function getObservabilityToolDomains(
  observabilityTool: ObservabilityTool,
  customDomainName: string | undefined,
  AWSDomains: AWSDomain[],
  responseData: TeamExternalIntegrationResponse
) {
  if (
    observabilityTool.type === ObservabilityToolType.CUSTOM &&
    customDomainName
  ) {
    return [
      {
        name: responseData.domain ?? '',
        integrationType: IntegrationType.Custom
      }
    ]
  }

  if (observabilityTool.type === ObservabilityToolType.AWS) {
    return AWSDomains
  }

  return OBSERVABILITY_TOOLS.find(({ type }) => type === observabilityTool.type)
    ?.domains
}

/**
 * Rebuilds the base form values using the response data received from authenticating a new Team Authentication and resets
 * the form state with the new Team Authentication already selected.
 */
export function refreshFormAfterObservabilityToolAuthentication(
  responseData: TeamExternalIntegrationResponse,
  integration: TeamExternalIntegration,
  externalIntegrations: TeamExternalIntegration[],
  form: UseFormReturn<StatusCheckFormValues | AuthenticationFormValues>
) {
  const { getValues, reset } = form
  const {
    customDomainName,
    domain = { name: '' },
    observabilityTool,
    isPrivateNetworkIntegration
  } = getValues()

  const isCustomIntegration = integration.type === IntegrationType.Custom

  // add back the new domain that just got added
  const AWSDomains = getAWSDomains(externalIntegrations)

  AWSDomains.push({
    name: responseData.domain ?? '',
    integrationType: IntegrationType.AWS
  })

  const domains = getObservabilityToolDomains(
    observabilityTool,
    customDomainName,
    AWSDomains,
    responseData
  )

  // Build newly created observability tool
  const newObservabilityTool: ObservabilityTool = {
    ...observabilityTool,
    name: integration.name,
    domains
  }

  if (integration.observabilityToolType === ObservabilityToolType.AWS) {
    newObservabilityTool.selectedDomain = responseData.domain ?? ''
    domain.name = responseData.domain ?? ''
  }

  if (integration.observabilityToolType === ObservabilityToolType.GRAFANA) {
    newObservabilityTool.name =
      OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.GRAFANA]
  }

  // Find new external integration
  const newExternalIntegration = getExternalIntegrationForObservabilityTool(
    newObservabilityTool,
    externalIntegrations
  )

  // Headers in the integration are returned as an object from the API. We need to map them to an array of keys and values
  const headers = Object.keys(responseData.headers).map((key) => ({
    key,
    value: responseData.headers[key]
  }))

  // Build newly configured integration from response data and retrieved integration object from context
  const newIntegration = {
    ...(isCustomIntegration
      ? { ...responseData, type: IntegrationType.Custom }
      : {
          ...newExternalIntegration,
          ...responseData,
          // the `type` in the responseData is in uppercase, but the downstream logic to convert the urls properly expects it to be in lowercase (see `completeStatusCheckIntegrations` in `team-external-integrations-util.ts`)
          type: getIntegrationTypeFromObservabilityToolTypeAndDomain(
            responseData.observabilityToolType,
            responseData.domain
          )
        }),
    headers,
    isPrivateNetworkIntegration,
    isConfigured: true
  }

  // Reset form
  reset()

  // Select newly created observability tool
  setObservabilityToolAndDomain(
    newObservabilityTool,
    domain,
    newIntegration,
    form
  )
}

/**
 * Gets translatable AWS authentication types
 */
export function getAWSAuthenticationTypes(
  language: TranslationLanguage = 'en'
) {
  return [
    {
      label: trans(
        'status_checks.authentication.aws.authentication_type.iam_role',
        {},
        language
      ),
      value: AWSAuthenticationType.IAMRole
    },
    {
      label: trans(
        'status_checks.authentication.aws.authentication_type.service_account',
        {},
        language
      ),
      value: AWSAuthenticationType.ServiceAccount
    }
  ]
}

/**
 * Gets translatable AWS role creation methods
 */
export function getAWSRoleCreationMethods(
  language: TranslationLanguage = 'en'
) {
  return [
    {
      label: trans(
        'status_checks.authentication.aws.role_creation_method.cloud_formation',
        {},
        language
      ),
      value: AWSRoleCreationMethod.CloudFormation
    },
    {
      label: trans(
        'status_checks.authentication.aws.role_creation_method.manual',
        {},
        language
      ),
      value: AWSRoleCreationMethod.Manual
    }
  ]
}

/**
 * Filters the base list of all observability tools to exclude those without a saved external integration
 */
export function getUnsavedObservabilityTools(
  unsavedExternalIntegrations: TeamExternalIntegration[],
  AWSDomains: AWSDomain[]
) {
  const unsavedExternalIntegrationsTypes = new Set(
    unsavedExternalIntegrations.map(({ type }) => type)
  )

  return OBSERVABILITY_TOOLS.reduce<ObservabilityTool[]>(
    (acc, sourceObservabilityTool) => {
      // Create a copy of the observability tool to avoid mutating the original
      const observabilityTool = { ...sourceObservabilityTool }
      const { domains, integrationType, name } = observabilityTool

      // Check if the observability tool has a corresponding unsaved external integration
      const isNotSaved =
        integrationType && unsavedExternalIntegrationsTypes.has(integrationType)

      // Filter out any domains that don't have a corresponding unsaved external integration
      observabilityTool.domains = domains?.filter(
        ({ integrationType }) =>
          integrationType &&
          unsavedExternalIntegrationsTypes.has(integrationType)
      )

      // If the observability tool is not saved or has domains, add it to the list
      if (isNotSaved || domains?.length) {
        if (name === 'AWS') {
          observabilityTool.domains = AWSDomains
        }

        acc.push(observabilityTool)
      }

      return acc
    },
    []
  )
}

export function getAuthenticationDomainNameForSave(
  formValues: Pick<
    AuthenticationFormValues,
    'customDomainName' | 'domain' | 'multipleRegions'
  >,
  { type }: TeamExternalIntegration
) {
  const { domain, customDomainName, multipleRegions } = formValues
  const isCustomIntegration = type === IntegrationType.Custom
  const staticDomainName = domain?.name === 'US1' ? null : domain?.name

  return isCustomIntegration && multipleRegions
    ? customDomainName
    : staticDomainName
}

/**
 * Builds the request body to save a new Authentication for an Observability Tool
 */
export function getAuthenticationRequestBody(
  authenticationType: AuthenticationType | undefined,
  integration: TeamExternalIntegration
): AuthenticationRequestBody {
  const isAWS = integration.type === IntegrationType.AWS
  const isAuthenticationTypeAWSSecretsManager =
    authenticationType === AuthenticationType.AWSSecretsManager

  const requestBody = {
    lastAuthenticationStatus:
      TeamExternalIntegrationAuthenticationStatus.Authenticated,
    name: integration.name,
    privateNetwork: integration.isPrivateNetworkIntegration,
    url: getIntegrationUrl(integration),
    ...(isAuthenticationTypeAWSSecretsManager
      ? {
          arn: integration.arn ?? ''
        }
      : {
          headers: prepareHeaders(integration) ?? {}
        }),
    ...(isAWS && {
      integrationSpecificValues: getAWSIntegrationSpecificValues(integration)
    })
  } as const

  return requestBody
}

/**
 * `getAWSOnboardingAuthenticationDefaultValues`
 * defaults to the first domain if one exists.
 * There are times we may want a blank slate and
 * not want to default to an existing domain.
 */
type AWSDefaultValues = Pick<
  AuthenticationFormValues,
  | 'awsAccessKeyID'
  | 'awsIamRoleArn'
  | 'awsSecretAccessKey'
  | 'awsAuthenticationType'
  | 'awsRoleCreationMethod'
  | 'domain'
  | 'externalIntegration'
  | 'observabilityTool'
>
export function getAWSDefaultValuesForReset(
  externalIntegrations: TeamExternalIntegration[]
): AuthenticationFormValues {
  const observabilityTool = OBSERVABILITY_TOOLS.find(
    (observabilityTool) =>
      observabilityTool.integrationType === IntegrationType.AWS
  )
  const externalIntegration = getExternalIntegrationForObservabilityTool(
    observabilityTool,
    externalIntegrations
  )

  const domains = getAWSDomains(externalIntegrations)

  const awsDefaultValues: AWSDefaultValues = {
    awsAccessKeyID: '',
    awsIamRoleArn: '',
    awsSecretAccessKey: '',
    awsAuthenticationType: AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    domain: { name: '', integrationType: IntegrationType.AWS },
    externalIntegration: externalIntegration,
    observabilityTool: {
      name: 'AWS',
      type: ObservabilityToolType.AWS,
      integrationType: IntegrationType.AWS,
      domains,
      selectedDomain: ''
    }
  }

  const defaultValues =
    getAWSOnboardingAuthenticationDefaultValues(externalIntegrations)

  return {
    ...defaultValues,
    ...awsDefaultValues
  }
}
